import { PRACTITIONER_APPOINTMENT_COLOR_CONTRAST } from '@liza/fhir-utils';
import type { PatientAdminApiRouterOutputs } from '@liza/patient-admin-api';
import { addMinutes, parseISO } from 'date-fns';
import type { ScheduleRow } from './scheduleRows';

/**
 * Turning schedule rows into the events a time grid places.
 *
 * Maps from `ScheduleRow` rather than from the tRPC payload on purpose: the list and the grid then read the *same*
 * interpretation of who a row's patient is and whether it is joinable, so the two views cannot disagree about a
 * visit. Everything locale-dependent stays out — the grid's own `eventContent` does the labeling, which keeps this
 * module pure and node-testable.
 */

/** The views the schedule offers. `list` is the day-grouped list; the rest are FullCalendar views. */
export const SCHEDULE_VIEWS = ['day', 'week'] as const;

export type ScheduleView = (typeof SCHEDULE_VIEWS)[number];

/** The view a schedule with nothing asked of it opens on. */
export const DEFAULT_SCHEDULE_VIEW: ScheduleView = 'day';

/** Whether an arbitrary value names a view — for URLs and stored preferences, neither of which is typed. */
export const isScheduleView = (value: unknown): value is ScheduleView => SCHEDULE_VIEWS.includes(value as ScheduleView);

/** FullCalendar's view name for a grid view. */
export const FULL_CALENDAR_VIEWS: Record<ScheduleView, string> = {
  day: 'timeGridDay',
  week: 'timeGridWeek',
};

/**
 * Minutes to draw an appointment that was stored without an `end`.
 *
 * Matches `DEFAULT_VISIT_MINUTES` in `packages/services/src/scheduling/appointmentRange.ts`, which the search uses
 * for the same reason — but this is a **display** fallback, not a claim about the visit. Medplum's calendar drops
 * end-less appointments entirely; we draw them, because a visit missing from a clinician's grid is the worse failure.
 */
const ASSUMED_VISIT_MINUTES = 60;

/** The instant a row's block should end: the stored end, or a nominal visit length after its start. */
export const rowEnd = (row: ScheduleRow): Date =>
  row.end ? parseISO(row.end) : addMinutes(parseISO(row.start), ASSUMED_VISIT_MINUTES);

/** Whether the block's length is assumed rather than stored — the grid marks these so the size isn't read as fact. */
export const hasAssumedEnd = (row: ScheduleRow): boolean => !row.end;

/**
 * The event a row renders as.
 *
 * No `title`: the patient's name must not reach a `title` attribute, because a native tooltip is not something
 * `.hide-phi [data-phi]` can blur. The name is rendered by `eventContent` instead, inside a `data-phi` element.
 */
export type ScheduleEvent = {
  id: string;
  start: Date;
  end: Date;
  /**
   * Where the block links to. Present only for a row whose patient the read composed — the rest have no chart to open.
   *
   * Load-bearing for **keyboard access**, not just for right-click: FullCalendar renders an event with a `url` as an
   * anchor, so it lands in the tab order and gets an accessible name from its own text. Without it a block is an inert
   * div reachable only by mouse, which the list view's `<Link>` is not.
   */
  url?: string;
  /**
   * The block's fill — the treating clinician's color, so a practice's day reads by who each visit is
   * with (#9416).
   *
   * Omitted rather than defaulted for a clinician with no color yet: FullCalendar then falls back to its
   * `eventColor` option, which the classic theme points at `--fc-classic-event` and `scheduleGrid.css`
   * remaps onto `--primary` — so an uncolored practice's grid looks exactly as it did.
   */
  color?: string;
  /** The block's text color. Set with (and only with) `color`, which the palette is chosen to carry. */
  contrastColor?: string;
  /** The row itself, so the grid's click and render hooks need no id lookup back into the payload. */
  extendedProps: { row: ScheduleRow };
};

/**
 * `hrefForRow` is injected rather than built here, so this module stays free of route knowledge — the component owns
 * the care-plan path, exactly as the list's `<Link>` does.
 */
export const toCalendarEvents = (
  rows: ScheduleRow[],
  hrefForRow: (row: ScheduleRow) => string | undefined,
): ScheduleEvent[] =>
  rows.map((row) => {
    const url = hrefForRow(row);
    return {
      id: row.id,
      start: parseISO(row.start),
      end: rowEnd(row),
      ...(url ? { url } : {}),
      ...(row.clinicianColor
        ? { color: row.clinicianColor, contrastColor: PRACTITIONER_APPOINTMENT_COLOR_CONTRAST }
        : {}),
      extendedProps: { row },
    };
  });

/** Reads the row back off an event's `extendedProps`, or `undefined` for an event this module did not create. */
export const eventRow = (extendedProps: unknown): ScheduleRow | undefined =>
  (extendedProps as { row?: ScheduleRow } | undefined)?.row;

/** The class the grid's CSS shades a blocked window with, and the hook its tests address one by. */
export const BLOCKED_EVENT_CLASS = 'schedule-blocked';

/** The class on a band's label. Named so the CSS need not reach through FullCalendar's own structure. */
export const BLOCKED_LABEL_CLASS = 'schedule-blocked-label';

/**
 * A window `$find` will not offer, as the API returns it: attributed to one clinician, and already merged
 * within that clinician, so a single person's windows never overlap each other.
 */
export type BlockedTime = PatientAdminApiRouterOutputs['scheduleAvailability']['listBlockedTimes']['blocked'][number];

/**
 * A blocked window's event. `display: 'background'` is the whole treatment — FullCalendar paints these behind
 * the day's blocks rather than beside them — and `interactive: false` keeps a shading out of the tab order.
 */
export type BlockedEvent = {
  id: string;
  start: Date;
  end: Date;
  /** Rendered as the block's only text by FullCalendar's default background-event content. */
  title: string;
  display: 'background';
  interactive: false;
  class: typeof BLOCKED_EVENT_CLASS;
};

/**
 * The blocked windows, cut into bands that never overlap each other.
 *
 * Clinicians' buffers coincide and partly overlap constantly. Drawn as they arrive those would stack:
 * background events paint over one another, so the translucent fill would compound into arbitrary darkness
 * and the labels would print on top of each other. So the day is cut at every window boundary, and each
 * band names everyone blocked across it. Adjacent bands blocking the same people rejoin, so a boundary that
 * changes nothing leaves no seam.
 *
 * Works in epoch milliseconds throughout. FHIR `instant` renders one moment several ways (`…T11:30:00Z`,
 * `…T11:30:00.000Z`, `…T11:30:00+00:00`), so comparing the strings would treat two spellings of a boundary
 * as two boundaries — a zero-width band between them, and a rejoin that never fires.
 */
const blockedBands = (blocked: BlockedTime[]): { start: Date; end: Date; practitionerIds: string[] }[] => {
  // Parsed once: the sweep below is quadratic in the window count, and `parseISO` is not cheap enough to
  // re-run per comparison on a practice's week.
  const spans = blocked.map((window) => ({
    startMs: parseISO(window.start).getTime(),
    endMs: parseISO(window.end).getTime(),
    practitionerId: window.practitionerId,
  }));
  const boundaries = [...new Set(spans.flatMap((span) => [span.startMs, span.endMs]))].sort((a, b) => a - b);

  const bands: { start: Date; end: Date; practitionerIds: string[] }[] = [];
  for (let index = 0; index < boundaries.length - 1; index += 1) {
    const startMs = boundaries[index]!;
    const endMs = boundaries[index + 1]!;
    const practitionerIds = spans
      .filter((span) => span.startMs <= startMs && span.endMs >= endMs)
      .map((span) => span.practitionerId);
    if (practitionerIds.length === 0) continue;

    const previous = bands[bands.length - 1];
    if (previous && previous.end.getTime() === startMs && previous.practitionerIds.join() === practitionerIds.join()) {
      previous.end = new Date(endMs);
    } else {
      bands.push({ start: new Date(startMs), end: new Date(endMs), practitionerIds });
    }
  }
  return bands;
};

/**
 * `labelFor` is injected rather than a catalog read here, so this module stays free of i18n exactly as it
 * stays free of route knowledge — the grid holds the `t` and the names these bands are titled from.
 */
export const toBlockedEvents = (
  blocked: BlockedTime[],
  labelFor: (practitionerIds: string[]) => string,
): BlockedEvent[] =>
  blockedBands(blocked).map((band) => ({
    // Identity from the span itself: no one Slot names a band, and two renders of it must agree.
    id: `blocked-${band.start.getTime()}-${band.end.getTime()}`,
    start: band.start,
    end: band.end,
    title: labelFor(band.practitionerIds),
    display: 'background',
    interactive: false,
    class: BLOCKED_EVENT_CLASS,
  }));
